import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

const travelStyles = [
  { value: 'adventure', label: 'Adventure' },
  { value: 'relaxation', label: 'Relaxation' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'budget_friendly', label: 'Budget Friendly' },
  { value: 'social', label: 'Social' },
  { value: 'wellness', label: 'Wellness' },
];

const popularDestinations = [
  'Greece', 'Italy', 'Spain', 'France', 'Japan', 'Thailand', 'Bali',
  'Dubai', 'Morocco', 'Portugal', 'Iceland', 'Switzerland', 'Australia',
  'New Zealand', 'Costa Rica', 'Mexico', 'Brazil', 'Peru', 'Egypt', 'India'
];

const TravelPreferencesStep = ({ formData, updateFormData }) => {
  const toggleDestination = (destination) => {
    const current = formData.preferred_destinations || [];
    if (current.includes(destination)) {
      updateFormData({
        preferred_destinations: current.filter(d => d !== destination)
      });
    } else {
      updateFormData({
        preferred_destinations: [...current, destination]
      });
    }
  };
  
  const toggleTravelStyle = (style) => {
    const current = formData.travel_styles || [];
    if (current.includes(style)) {
      updateFormData({
        travel_styles: current.filter(s => s !== style)
      });
    } else {
      updateFormData({
        travel_styles: [...current, style]
      });
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-neutral-600 mb-6">
        Tell us about your travel preferences
      </p>
      
      {/* Travel Styles */}
      <div className="space-y-3">
        <Label>Travel Styles *</Label>
        <p className="text-sm text-neutral-500">Select all that apply</p>
        <div className="flex flex-wrap gap-2">
          {travelStyles.map((style) => (
            <Badge
              key={style.value}
              variant={formData.travel_styles?.includes(style.value) ? 'default' : 'outline'}
              className="cursor-pointer px-4 py-2 text-sm"
              onClick={() => toggleTravelStyle(style.value)}
            >
              {style.label}
            </Badge>
          ))}
        </div>
      </div>
      
      {/* Preferred Destinations */}
      <div className="space-y-3">
        <Label>Preferred Destinations *</Label>
        <p className="text-sm text-neutral-500">Select your dream destinations</p>
        <div className="flex flex-wrap gap-2">
          {popularDestinations.map((destination) => (
            <Badge
              key={destination}
              variant={formData.preferred_destinations?.includes(destination) ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1.5 text-sm"
              onClick={() => toggleDestination(destination)}
            >
              {destination}
            </Badge>
          ))}
        </div>
      </div>
      
      {/* Budget Range */}
      <div className="space-y-3">
        <Label>Budget Range (USD per trip)</Label>
        <div className="px-2">
          <Slider
            value={[formData.budget_min, formData.budget_max]}
            onValueChange={([min, max]) => updateFormData({ budget_min: min, budget_max: max })}
            min={500}
            max={10000}
            step={100}
            className="mb-4"
          />
          <div className="flex justify-between text-sm">
            <span className="font-semibold text-primary-700">${formData.budget_min}</span>
            <span className="font-semibold text-primary-700">${formData.budget_max}</span>
          </div>
        </div>
      </div>
      
      {/* Travel Frequency */}
      <div className="space-y-2">
        <Label htmlFor="travel_frequency">How often do you travel?</Label>
        <Select
          value={formData.travel_frequency}
          onValueChange={(value) => updateFormData({ travel_frequency: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Once a month</SelectItem>
            <SelectItem value="quarterly">Every 3 months</SelectItem>
            <SelectItem value="biannually">Twice a year</SelectItem>
            <SelectItem value="annually">Once a year</SelectItem>
            <SelectItem value="rarely">Rarely</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Group Size Preference */}
      <div className="space-y-2">
        <Label htmlFor="group_size">Preferred Group Size</Label>
        <Select
          value={formData.preferred_group_size}
          onValueChange={(value) => updateFormData({ preferred_group_size: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select group size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Small (4-8 people)</SelectItem>
            <SelectItem value="medium">Medium (8-15 people)</SelectItem>
            <SelectItem value="large">Large (15+ people)</SelectItem>
            <SelectItem value="any">Any size</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Solo Travel */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="solo_travel"
          checked={formData.willing_to_travel_solo}
          onCheckedChange={(checked) => updateFormData({ willing_to_travel_solo: checked })}
        />
        <label
          htmlFor="solo_travel"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          I'm willing to travel solo
        </label>
      </div>
    </div>
  );
};

export default TravelPreferencesStep;
